import { observer } from 'mobx-react';
import React from 'react';
import { Form, FormFeedback, FormGroup, Input } from 'reactstrap';
import BusyButton from '~/components/BusyButton';
import ErrorList from '~/components/ErrorList';
import PasswordInput from '~/components/PasswordInput';
import { LoginRoute, resourceListRoute, ResourceType, SignupRoute } from '~/lib/helper/RouteHelper';
import { field, is, validate } from '~/lib/helper/ValidationHelper';
import FormStore from '~/lib/store/FormStore';
import { getAppContext } from '~/lib/AppContext';
import { action, makeObservable } from 'mobx';
import UnmountHelper from '~/lib/helper/UnmountHelper';
import {
    PASSWORD_MIN_LENGTH,
    PASSWORD_MAX_LENGTH,
    PASSWORD_REQUIREMENTS_TEXT,
} from '~/lib/constants/PasswordValidation';

interface RegistrationFormValues {
    email: string;
    password: string;
}

function emptyFormValues(): RegistrationFormValues {
    return {
        email: '',
        password: '',
    };
}

@observer
export default class RegistrationForm extends React.Component {
    registrationForm = new FormStore<RegistrationFormValues>(emptyFormValues());

    unmountHelper = new UnmountHelper(this);

    constructor(props: Record<string, never>) {
        super(props);
        makeObservable(this);
        this.init();
    }

    componentDidMount() {
        void this.redirectIfLoggedIn();
    }

    async redirectIfLoggedIn() {
        try {
            await this.unmountHelper.cancelIfUnmounted(getAppContext().customer.ensureLoaded());
            // if we didn't throw 401 we are already logged in.
            getAppContext().navigate(resourceListRoute(ResourceType.Server));
        } catch {
            // this is expected if they aren't logged in.
        }
    }

    @action
    init() {
        this.registrationForm.init(emptyFormValues());
    }

    async doRegister() {
        if (
            !this.registrationForm.validate((e) =>
                validate(
                    e,
                    field('email', is.requiredNotEmpty('Email is required')),
                    field(
                        'password',
                        is.requiredNotEmpty('Password is required'),
                        is.minLength(
                            PASSWORD_MIN_LENGTH,
                            `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`
                        ),
                        is.maxLength(
                            PASSWORD_MAX_LENGTH,
                            `Password must be at most ${PASSWORD_MAX_LENGTH} characters long`
                        )
                    )
                )
            )
        ) {
            return;
        }

        const registerRequest = {
            email: this.registrationForm.values.email,
            password: this.registrationForm.values.password,
        };

        const registerResult = await getAppContext().siteApiClient.register(registerRequest);

        if (registerResult.login_result) {
            // If login_result is not null, user was logged in, redirect to /services
            return getAppContext().navigate(resourceListRoute(ResourceType.Server));
        } else {
            // Otherwise redirect to /signup
            return getAppContext().navigate(SignupRoute);
        }
    }

    render() {
        const { registrationForm } = this;
        const appConfig = getAppContext().config;
        return (
            <>
                <div className="preLoginTitle">
                    <h2>Let's get started.</h2>
                    <p>These are the login details you will use to access the {appConfig.brandName} website.</p>
                </div>
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        void registrationForm.doSubmit(() => this.doRegister(), {
                            errorSummary: 'Unable to create account',
                        });
                    }}
                >
                    <FormGroup className="mb-3">
                        <label className="form-label" htmlFor={registrationForm.idFor('email')}>
                            Email address
                        </label>
                        <Input
                            id={registrationForm.idFor('email')}
                            type="email"
                            autoComplete="username"
                            autoFocus
                            placeholder="Enter your email"
                            {...registrationForm.inputProps('email')}
                        />
                        <FormFeedback>
                            <ErrorList errors={registrationForm.errorsFor('email')} />
                        </FormFeedback>
                    </FormGroup>

                    <FormGroup className="mb-4">
                        <label className="form-label" htmlFor={registrationForm.idFor('password')}>
                            Password
                        </label>
                        <PasswordInput
                            id={registrationForm.idFor('password')}
                            autoComplete="new-password"
                            placeholder="Enter your password"
                            {...registrationForm.inputProps('password')}
                        />
                        <small className="form-text text-muted">{PASSWORD_REQUIREMENTS_TEXT}</small>
                        <ErrorList errors={registrationForm.errorsFor('password')} />
                    </FormGroup>

                    <div className="loginActions">
                        <BusyButton
                            type="submit"
                            busy={registrationForm.isSubmitting}
                            color="primary"
                            size="lg"
                            className="w-100"
                        >
                            <i className="icon ion-md-person-add me-2" />
                            Create Account
                        </BusyButton>
                    </div>

                    <div className="text-muted mt-3 text-center fs-0-9rem">
                        <a href={LoginRoute}>Already have an account?</a>
                    </div>
                </Form>
            </>
        );
    }
}
